import React from 'react'
import { theme } from 'antd'
import { radius } from '../../constants/ThemeSetting'
import { energyModeHierarchy, getEnergyMode } from '../../functions/user-insights/determine-energy-level'
import { getColorForEnergyMode } from '../../functions/user-insights/energy-visualization'

export type EnergyIndicatorSize = 'badge' | 'small' | 'default'

/** Which way the battery stands. Vertical is what the avatar badge and the check-in picker use. */
export type EnergyIndicatorOrientation = 'horizontal' | 'vertical'

export interface EnergyIndicatorProps {
  /** 0-100, or 0-1 (both scales are in use across the app). Undefined renders the not-calculated
   * state: "no data" and "no energy" are different answers and must not look alike. */
  score?: number
  size?: EnergyIndicatorSize
  orientation?: EnergyIndicatorOrientation
  /** Accessible name, e.g. "Energy: Energized". Supplied by the caller so the copy stays in
   * the caller's i18n catalogue rather than hardcoded in the design system. */
  label: string
  /** Whether `label` is also a native `title`. Off when something around it already explains the
   * reading on hover - a browser tooltip firing underneath a hover card is two answers to one
   * gesture. The accessible name is unaffected either way. */
  showTitle?: boolean
  className?: string
}

/** One bar per energy mode, so the reading is countable. */
const SEGMENT_COUNT = energyModeHierarchy.length

/**
 * `thickness` is measured along the bars' stacking direction, `across` at right angles to it, so
 * one table serves both orientations.
 *
 * `badge` exists for the avatar badge, where `small` still overhangs a 32px circle. What has to
 * survive at that size is the bar COUNT, so the bars shrink and the gaps stay - bars that touch
 * each other cannot be counted.
 */
const SIZES: Record<EnergyIndicatorSize, { thickness: number; across: number; gap: number; capAcross: number }> = {
  badge: { thickness: 2, across: 9, gap: 1, capAcross: 4 },
  small: { thickness: 5, across: 12, gap: 2, capAcross: 6 },
  default: { thickness: 8, across: 18, gap: 3, capAcross: 8 },
}

/** energyModeHierarchy runs strongest-first, so a mode's distance from the end of that list IS its
 * reading - this can never disagree with the mode the rest of the app shows for the same score. */
export function filledSegmentsFor(score: number): number {
  const normalized = score > 1 ? score : score * 100
  return SEGMENT_COUNT - energyModeHierarchy.indexOf(getEnergyMode(normalized))
}

/**
 * Vacation Tracker's energy reading, as a battery.
 *
 * Colour comes from the product's own energy palette (`--energy-*-primary`, defined in
 * `styles/pages/energy-level.less`), so the battery, the avatar ring and the Energy Level page
 * cannot drift to different greens. Everything else is an antd theme token, so both themes work
 * without a stylesheet of its own.
 *
 * Colour is never the only channel: the number of filled bars carries the same reading, which is
 * what makes a partial level readable at row size.
 */
export function EnergyIndicator({ score, size = 'default', orientation = 'horizontal', label, showTitle = true, className }: EnergyIndicatorProps) {
  const { token } = theme.useToken()
  const { thickness, across, gap, capAcross } = SIZES[size]

  const calculated = score !== undefined
  const filled = calculated ? filledSegmentsFor(score) : 0
  const fillColor = calculated ? getColorForEnergyMode(getEnergyMode(score > 1 ? score : score * 100)) : undefined
  const outline = calculated ? token.colorBorder : token.colorBorderSecondary

  // Vertical fills from the BOTTOM, the way a battery drains. `column-reverse` does that without
  // reversing the array, which would put the bars in the DOM in the opposite order to the reading.
  const vertical = orientation === 'vertical'

  // No reading means no BAR TRACK at all, replaced by a '?': five empty bars sit one 2px sliver of
  // colour away from a real Drained reading, so "not measured" reads as "running on empty". The
  // glyph scales off the shell so it stays legible at badge size.
  const glyphSize = Math.max(8, across - 2 * gap)

  return (
    <span
      className={className}
      role="img"
      aria-label={label}
      title={showTitle ? label : undefined}
      style={{
        display: 'inline-flex',
        flexDirection: vertical ? 'column' : 'row',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
      }}
    >
      {/* The terminal nub, drawn before the shell when vertical: on a battery standing up it
          belongs on TOP, and source order is what decides which end it sits at. */}
      {vertical && <BatteryCap vertical color={outline} across={capAcross} />}
      <span
        style={{
          display: 'inline-flex',
          flexDirection: vertical ? 'column-reverse' : 'row',
          alignItems: 'center',
          gap,
          ...(vertical ? { width: across } : { height: across }),
          // The shell must keep the size it has when full, or an unmeasured badge would be a
          // different shape from a measured one and the column would jog as scores arrive.
          ...(vertical ? { minHeight: SEGMENT_COUNT * thickness + (SEGMENT_COUNT - 1) * gap } : { minWidth: SEGMENT_COUNT * thickness + (SEGMENT_COUNT - 1) * gap }),
          justifyContent: 'center',
          padding: gap,
          border: `1px solid ${outline}`,
          // The app's smallest radius. Everything INSIDE the shell is square: 4 on a 9px-wide badge
          // is already nearly a stadium, so rounding the bars too makes the glyph read softer than
          // anything around it.
          borderRadius: radius.sm,
        }}
      >
        {calculated &&
          Array.from({ length: SEGMENT_COUNT }, (_, index) => (
            <span
              key={index}
              style={{
                ...(vertical ? { height: thickness, width: '100%' } : { width: thickness, height: '100%' }),
                borderRadius: 0,
                background: index < filled ? fillColor : token.colorFillSecondary,
              }}
            />
          ))}
        {!calculated && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: glyphSize,
              lineHeight: 1,
              fontWeight: 600,
              color: token.colorTextTertiary,
            }}
          >
            ?
          </span>
        )}
      </span>
      {!vertical && <BatteryCap color={outline} across={capAcross} />}
    </span>
  )
}

/** Square, like the bars - see the shell's own note. */
function BatteryCap({ vertical = false, color, across }: { vertical?: boolean; color: string; across: number }) {
  return (
    <span
      style={{
        ...(vertical ? { width: across, height: 2 } : { width: 2, height: across }),
        background: color,
      }}
    />
  )
}

export default EnergyIndicator
